<template>
    <div class="relative flex flex-col justify-center items-center w-full h-full">
        <div
            class="
                relative flex justify-center items-center border-dashed rounded
                transition duration-300
            "
            :class="[dropzoneClassName, compact ? 'w-64 h-56 mb-4 border' : 'w-64 h-64 mb-6 border-2']"
            drag-over="handleDragOver"
            @dragenter="isHovering = true"
            @dragleave="isHovering = false"
            v-tooltip="{ content: hasError ? $t('imports.upload.invalidFileType') : '', placement: 'bottom' }"
        >
            <nio-icon :class="iconClassName" :name="icon" />

            <input
                class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                :disabled="isUploading"
                type="file"
                ref="uploadInput"
                @change="fileChanged"
            >
        </div>

        <div class="w-full flex justify-center items-baseline overflow-hidden">
            <span
                class="truncate"
                :class="compact ? 'text-xl' : 'text-2xl'"
                v-text="hasFile ? file.name : $t('imports.upload.dropFile')"
            />
            <button
                class="flex-shrink-0 inline-flex items-center ml-2 | transition duration-300 hover:text-red-600"
                :class="[{ 'disabled': isUploading }, compact ? 'text-base' : 'text-lg']"
                @click="removeFile"
                v-tooltip="$t('imports.upload.removeFile')"
                v-if="hasFile"
            >
                <nio-icon name="bold/remove-circle" />
            </button>
        </div>
        <p class="text-center" :class="compact ? 'mt-1 text-xs' : 'mt-2'">
            {{ $t('imports.upload.allowedFileType', { types: allowedFileTypes }) }}
        </p>
    </div>
</template>

<script>
    const FILE_TYPES = [
        {
            name: 'CSV',
            extension: 'csv',
            mimeTypes: ['text/csv', 'application/vnd.ms-excel'],
            icon: 'light/file-csv',
        },
        {
            name: 'XLSX',
            extension: 'xlsx',
            mimeTypes: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            icon: 'light/office-file-xls',
        },
        {
            name: 'PDF',
            extension: 'pdf',
            mimeTypes: 'application/pdf',
            icon: 'light/office-file-pdf',
        },
        {
            name: 'JPG',
            extension: 'jpg',
            mimeTypes: 'image/jpeg',
            icon: 'light/image-file-jpg',
        },
        {
            name: 'JPEG',
            extension: 'jpeg',
            mimeTypes: 'image/jpeg',
            icon: 'light/image-file-jpg',
        },
        {
            name: 'PNG',
            extension: 'png',
            mimeTypes: 'image/png',
            icon: 'light/image-file-png',
        },
    ];

    const EXTENSIONS = Object.values(FILE_TYPES).map(fileType => fileType.extension);

    export default {
        props: {
            value: {
                type: File,
                default: null,
            },
            fileTypes: {
                type: Array,
                required: true,
                validator: values => {
                    return values.some(value => EXTENSIONS.includes(value));
                },
            },
            isUploading: {
                type: Boolean,
                default: false,
            },
            compact: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                file: this.value,
                isHovering: false,
                hasError: false,
            };
        },

        computed: {
            icon() {
                if (this.hasError) {
                    return 'regular/common-file-disable';
                }

                if (!this.hasFile) {
                    return 'light/upload-bottom';
                }

                return this.getFileType(this.file.type)?.icon || 'regular/common-file-text';
            },

            iconClassName() {
                if (this.compact) {
                    return this.hasFile ? 'h-11 w-11' : 'h-9 w-9';
                }

                return this.hasFile ? 'h-13 w-13' : 'h-11 w-11';
            },

            hasFile() {
                return !!this.file;
            },

            dropzoneClassName() {
                if (this.hasError) {
                    return [
                        'text-yellow-400 border-yellow-500',
                        'hover:text-yellow-500 hover:border-yellow-600',
                        { 'text-yellow-500 border-yellow-600': this.isHovering },
                    ];
                }

                return [
                    'text-gray-500 border-gray-500',
                    'hover:text-blue-500 hover:border-blue-500',
                    { 'text-blue-500 border-blue-500': this.isHovering },
                ];
            },

            allowedFileTypes() {
                const allowedFileTypes = FILE_TYPES
                    .filter(f => this.fileTypes.includes(f.extension) && f.extension !== 'jpeg')
                    .map(f => f.name);

                return allowedFileTypes.join(', ');
            },
        },

        watch: {
            file(newValue) {
                if (!newValue) {
                    this.$emit('input', null);
                    return;
                }

                const fileType = this.getFileType(newValue.type);
                const extension = fileType?.extension || '';

                const isValidType = this.fileTypes.includes(extension);
                const isValidExtension = newValue.name.endsWith(`.${extension}`);

                if (!isValidType || !isValidExtension) {
                    this.hasError = true;
                } else {
                    this.hasError = false;
                    this.$emit('input', this.file);
                }
            },

            hasError(newValue) {
                this.$emit('error', newValue);
            },
        },

        methods: {
            fileChanged(event) {
                this.isHovering = false;

                const files = event.target.files || event.dataTransfer.files;

                if (!files.length) {
                    return;
                }

                this.resetErrors();
                this.file = files[0];
            },

            removeFile() {
                if (this.isUploading) {
                    return;
                }

                this.file = null;
                this.resetErrors();

                this.$refs.uploadInput.value = '';
            },

            resetErrors() {
                this.hasError = false;
            },

            getFileType(mimeType) {
                return FILE_TYPES.find(fileType => {
                    return Array.isArray(fileType.mimeTypes)
                        ? fileType.mimeTypes.includes(mimeType)
                        : mimeType === fileType.mimeTypes;
                });
            },
        },
    };
</script>
